import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBell, FiInbox, FiCheck, FiInfo, FiCheckCircle, FiAlertTriangle, FiAlertCircle, FiArrowRight,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { Spinner } from '../../components/ui/Spinner';
import { useDadosVivos } from '../../hooks/useDadosVivos';
import { notificacoesService, type Notificacao } from '../../services/notificacoes.service';

function tempoRel(iso: string): string {
  const seg = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seg < 60) return 'agora há pouco';
  const min = Math.floor(seg / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'ontem';
  if (d < 7) return `há ${d} dias`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

export function NotificacoesPage() {
  const { cidadao } = useAuth();
  const navigate = useNavigate();
  const [aba, setAba] = useState<'todas' | 'nao_lidas'>('todas');

  const { dados, carregando, recarregar } = useDadosVivos<Notificacao[]>(
    () => notificacoesService.listar(cidadao!.id),
    [cidadao?.id],
    { ativo: !!cidadao, intervalo: 60_000 },
  );
  const lista = dados ?? [];

  const naoLidas = lista.filter((n) => !n.lida).length;
  const exibidas = useMemo(
    () => (aba === 'nao_lidas' ? lista.filter((n) => !n.lida) : lista),
    [lista, aba],
  );

  async function abrir(n: Notificacao) {
    if (!n.lida) {
      await notificacoesService.marcarComoLida(n.id);
      recarregar();
    }
    if (n.link) navigate(n.link);
  }

  async function marcarTodas() {
    if (!cidadao) return;
    await notificacoesService.marcarTodasComoLidas(cidadao.id);
    recarregar();
  }

  return (
    <PortalPage
      titulo="Notificações"
      subtitulo={naoLidas > 0 ? `${naoLidas} não lida(s)` : 'Avisos sobre suas solicitações junto à prefeitura.'}
      icone={<FiBell size={26} />}
      voltarHref="/"
      largura="media"
      acoes={
        naoLidas > 0 ? (
          <button
            type="button"
            onClick={marcarTodas}
            className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 transition hover:bg-white/20"
          >
            <FiCheck size={15} /> Marcar todas
          </button>
        ) : undefined
      }
    >
      <div className="space-y-5">
        <div className="inline-flex gap-1 rounded-xl border border-slate-200 bg-white p-1">
          {(['todas', 'nao_lidas'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setAba(v)}
              className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition ${
                aba === v ? 'bg-institutional text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {v === 'todas' ? 'Todas' : `Não lidas${naoLidas > 0 ? ` (${naoLidas})` : ''}`}
            </button>
          ))}
        </div>

        {carregando ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : exibidas.length === 0 ? (
          <SectionCard>
            <div className="py-12 text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                <FiInbox className="text-slate-400" size={26} />
              </div>
              <p className="text-sm font-medium text-slate-700">
                {aba === 'nao_lidas' ? 'Nenhuma notificação não lida' : 'Você ainda não tem notificações'}
              </p>
              <p className="mt-1 text-xs text-slate-500">Avisaremos aqui quando houver novidades nas suas solicitações.</p>
            </div>
          </SectionCard>
        ) : (
          <SectionCard semPadding>
            <ul className="divide-y divide-slate-100">
              {exibidas.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => abrir(n)}
                    className={`flex w-full items-start gap-3 p-4 text-left transition hover:bg-slate-50 ${!n.lida ? 'bg-institutional/5' : ''}`}
                  >
                    <IconePorTipo tipo={n.tipo} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-slate-900">{n.titulo}</span>
                        {!n.lida && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-institutional" />}
                      </div>
                      <p className="mt-0.5 text-sm text-slate-600">{n.mensagem}</p>
                      <p className="mt-1 text-[11px] text-slate-400">{tempoRel(n.criado_em)}</p>
                    </div>
                    {n.link && <FiArrowRight className="mt-1 flex-shrink-0 text-slate-300" size={16} />}
                  </button>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}
      </div>
    </PortalPage>
  );
}

function IconePorTipo({ tipo }: { tipo: 'info' | 'sucesso' | 'aviso' | 'erro' }) {
  const map = {
    info: { Icone: FiInfo, cor: 'text-blue-500' },
    sucesso: { Icone: FiCheckCircle, cor: 'text-emerald-500' },
    aviso: { Icone: FiAlertTriangle, cor: 'text-amber-500' },
    erro: { Icone: FiAlertCircle, cor: 'text-red-500' },
  } as const;
  const { Icone, cor } = map[tipo] ?? map.info;
  return <div className={`mt-0.5 flex-shrink-0 ${cor}`}><Icone size={18} /></div>;
}
